import { TNestedGenericId, TOrderedSelectDataItem, TSelectDataItem } from '../types'
import {
    ELanguage,
    FORTIWEB_IP,
    INTERNATIONAL_CITY_ID,
    NAVIGOS_COMPANY_ID,
    VIETNAM_COUNTRY_ID,
    pathPage
} from './constants'
import translations from './translations'

export function filterInt(value: string) {
    if (/^[-+]?(\d+|Infinity)$/.test(value)) {
        return Number(value)
    } else {
        return NaN
    }
}

export function isMobile(): boolean {
    if (typeof window !== 'undefined') {
        return window.innerWidth < 768
    }
    return false
}

export function isTablet(): boolean {
    if (typeof window !== 'undefined') {
        return window.innerWidth >= 768 && window.innerWidth < 992
    }
    return false
}

export function isDesktop(): boolean {
    if (typeof window !== 'undefined') {
        return window.innerWidth >= 992
    }
    return false
}

// Hex to RGBA with 20% opacity
export function ConvertHexToRgbA(hex: string, opacity: string) {
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        let c: any = hex.substring(1).split('')
        if (c.length === 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]]
        }
        c = '0x' + c.join('')
        return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + `,${opacity})`
    }
    throw new Error('Bad Hex')
}

export function addCommaToNumber(number: number | string): string {
    const nums = number.toString().replace(/,/g, '')
    if (!nums || nums.endsWith('.')) return number.toString()
    return parseFloat(nums).toLocaleString('en-US')
}

export function getNumberFromString(string: string) {
    if (string) {
        return Number(string.replace(/,/g, ''))
    } else return ''
}

export function decodeKeyword(encodedKeyword: string) {
    return encodedKeyword
        .split(/[-]{2,}/g)
        .join('[TEMP DOUBLE HYPHEN]')
        .split('-')
        .join(' ')
        .split('[TEMP DOUBLE HYPHEN]')
        .join('-')
        .replace(/\+\+/g, '_plus_plus')
        .replace(/\+/g, '%20')
        .replace(/C_plus_plus|c_plus_plus/, 'C++')
        .replace(/C_sharp|c_sharp/, 'C#')
}

export function encodeKeyword(rawKeyword: string) {
    const blackList = [
        { regex: /C#/gi, replace: 'C_sharp' },
        {
            regex: /C\+\+/gi,
            replace: 'C_plus_plus'
        },
        { regex: /[[\]\\/:~^?!()><"'@%|{}`*#]/gi, replace: ' ' },
        { regex: /&/gi, replace: encodeURIComponent('&') }
    ]
    let tempkeyword

    for (const i in blackList) {
        rawKeyword = rawKeyword.replace(blackList[i].regex, blackList[i].replace)
    }
    do {
        tempkeyword = rawKeyword
        rawKeyword = tempkeyword.replace('  ', ' ')
    } while (tempkeyword !== rawKeyword)

    rawKeyword = rawKeyword.replace(/[-]{1,}/g, '--')

    rawKeyword = rawKeyword.trim()
    return rawKeyword.replace(/ /g, '-')
}

/**
 * Transform all characters with accent into latin, e.g. `Ô kìa Con vịt` to `o kia con vit`
 * @param text - the text you want to transform
 */
export function transformToLatin(text: string, isUrl: boolean = false) {
    text = text.toString().toLowerCase().trim()

    const sets = [
        { to: 'a', from: '[ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶἀ]' },
        { to: 'c', from: '[ÇĆĈČ]' },
        { to: 'd', from: '[ÐĎĐÞ]' },
        { to: 'e', from: '[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ]' },
        { to: 'g', from: '[ĜĞĢǴ]' },
        { to: 'h', from: '[ĤḦ]' },
        { to: 'i', from: '[ÌÍÎÏĨĪĮİỈỊ]' },
        { to: 'j', from: '[Ĵ]' },
        { to: 'ij', from: '[Ĳ]' },
        { to: 'k', from: '[Ķ]' },
        { to: 'l', from: '[ĹĻĽŁ]' },
        { to: 'm', from: '[Ḿ]' },
        { to: 'n', from: '[ÑŃŅŇ]' },
        { to: 'o', from: '[ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ]' },
        { to: 'oe', from: '[Œ]' },
        { to: 'p', from: '[ṕ]' },
        { to: 'r', from: '[ŔŖŘ]' },
        { to: 's', from: '[ßŚŜŞŠȘ]' },
        { to: 't', from: '[ŢŤ]' },
        { to: 'u', from: '[ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ]' },
        { to: 'w', from: '[ẂŴẀẄ]' },
        { to: 'x', from: '[ẍ]' },
        { to: 'y', from: '[ÝŶŸỲỴỶỸ]' },
        { to: 'z', from: '[ŹŻŽ]' }
    ]

    if (!isUrl) {
        sets.push({ to: '-', from: "[·/_,:;']" })
    }

    sets.forEach((set) => {
        text = text.replace(new RegExp(set.from, 'gi'), set.to)
    })

    text = text.replace(/[^ -~]/gi, '')

    return text
}

/**
 * Clean slugs by removing spaces, invalid characters and dashes
 * @param slug - the slug you wanna clean
 */
export function slugify(slug: string) {
    slug = slug.replace(/^\s+|\s+$/g, '') // trim
    slug = slug.toLowerCase()

    // remove accents
    slug = transformToLatin(slug)

    slug = slug
        .replace(/[^a-z0-9 -]/g, '-') // invalid chars to -
        .replace(/\s+/g, '-') // collapse whitespace and replace by -
        .replace(/-+/g, '-') // collapse dashes

    return slug
}

export function t(key: string, language: string | ELanguage) {
    if (language === 'en') {
        return key
    } else {
        return translations[key] ?? key
    }
}
/**
 * Decrypt accessToken (Copied from app-shell-v2/src/helpers/helper.ts:245 )
 * @param token accessToken (Example: user.accessToken from homepage)
 * @returns decrypted accessToken
 */
export function decrypt(token: string): string {
    if (typeof atob === 'function') {
        return atob(atob(token))
    }

    const nodeAtob = function (str: string) {
        return Buffer.from(str, 'base64').toString('binary')
    }

    return nodeAtob(nodeAtob(token))
}

export function encrypt(token: string): string {
    if (typeof btoa === 'function') {
        return btoa(btoa(token)).replace(/=/g, '')
    }
    const nodeBtoa = function (str: string) {
        return Buffer.from(str).toString('base64')
    }

    return nodeBtoa(nodeBtoa(token))
}

export function homePageSlugify(text: string, separator: string | null = null) {
    text = text.toString().toLowerCase().trim()

    const sets = [
        { to: 'a', from: '[ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶ]' },
        { to: 'c', from: '[ÇĆĈČ]' },
        { to: 'd', from: '[ÐĎĐÞ]' },
        { to: 'e', from: '[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ]' },
        { to: 'g', from: '[ĜĞĢǴ]' },
        { to: 'h', from: '[ĤḦ]' },
        { to: 'i', from: '[ÌÍÎÏĨĪĮİỈỊ]' },
        { to: 'j', from: '[Ĵ]' },
        { to: 'ij', from: '[Ĳ]' },
        { to: 'k', from: '[Ķ]' },
        { to: 'l', from: '[ĹĻĽŁ]' },
        { to: 'm', from: '[Ḿ]' },
        { to: 'n', from: '[ÑŃŅŇ]' },
        { to: 'o', from: '[ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ]' },
        { to: 'oe', from: '[Œ]' },
        { to: 'p', from: '[ṕ]' },
        { to: 'r', from: '[ŔŖŘ]' },
        { to: 's', from: '[ßŚŜŞŠ]' },
        { to: 't', from: '[ŢŤ]' },
        { to: 'u', from: '[ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ]' },
        { to: 'w', from: '[ẂŴẀẄ]' },
        { to: 'x', from: '[ẍ]' },
        { to: 'y', from: '[ÝŶŸỲỴỶỸ]' },
        { to: 'z', from: '[ŹŻŽ]' },
        { to: '-', from: "[·/_,:;']" }
    ]

    sets.forEach((set) => {
        text = text.replace(new RegExp(set.from, 'gi'), set.to)
    })

    text = text
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(/&/g, '-and-') // Replace & with 'and'
        .replace(/[^\w-]+/g, '') // Remove all non-word chars
        .replace(/--+/g, '-') // Replace multiple - with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, '') // Trim - from end of text

    if (typeof separator !== 'undefined' && separator !== null && separator !== '-') {
        text = text.replace(/-/g, separator)
    }

    return text
}

export const detectPage = () => {
    if (typeof window === undefined) {
        return 'search-result'
    }
    const url = window.location.href
    for (let i = 0; i < pathPage.length; i++) {
        const pathItem = pathPage[i]
        const checkPath = pathItem.path.filter((path) => url.indexOf(path) >= 0)
        if (checkPath.length > 0) {
            return pathItem.page
        }
    }
    return 'homepage'
}

export const filterHTMLTag = (text: string = '') => {
    return text !== undefined && text !== null ? text.replace(/<\/?[^>]+(>|$)/g, '') : ''
}

export function generateJobUrl(
    jobId: number,
    alias: string,
    lang: string = 'en',
    domain: string = '',
    utmString: string = ''
): string {
    let linkJob = domain + '/' + alias + '-' + jobId
    if (lang === 'en') {
        linkJob += '-jd'
    } else {
        linkJob += '-jv'
    }
    if (utmString.length > 0) {
        linkJob += '?' + utmString
    }
    return linkJob
}

export const genId = (length: number = 3) => {
    let result: string = ''
    const characters: string = '0123456789'
    const charactersLength: number = characters.length
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return parseInt(result)
}

export const compareVariable = (val1: any, val2: any) => {
    return JSON.stringify(val1) === JSON.stringify(val2)
}

export function hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    if (!result) {
        return null
    }
    const { r, g, b } = {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    }
    return {
        r,
        g,
        b,
        toString() {
            return [r, g, b].join(',')
        }
    }
}

export function isDifferentArrayData(arr1: any[], arr2: any[]): boolean {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
        console.trace(`[isDifferentArrayData] Invalid input data ${arr1} ${arr2}`)
        return true
    }
    return arr1.some((item) => !arr2.includes(item)) || arr2.some((item) => !arr1.includes(item))
}

export const sortArray = (options: any[], type: 'asc' | 'desc' = 'asc', objectKey: string = '') => {
    if (options.length > 0) {
        switch (typeof options[0]) {
            case 'number':
                return options.sort((a, b) => (type === 'asc' ? a - b : b - a))
            case 'string':
                return options.sort((a, b) => {
                    if (transformToLatin(a) < transformToLatin(b)) {
                        return type === 'asc' ? 1 : -1
                    }
                    if (transformToLatin(a) > transformToLatin(b)) {
                        return type === 'asc' ? -1 : 1
                    }
                    return 0
                })
            case 'object': {
                const firstObject = options[0]
                switch (typeof firstObject[objectKey]) {
                    case 'number':
                        return options.sort((a, b) =>
                            type === 'asc' ? a[objectKey] - b[objectKey] : b[objectKey] - a[objectKey]
                        )
                    case 'string':
                        return options.sort((a, b) => {
                            if (transformToLatin(a[objectKey]) < transformToLatin(b[objectKey])) {
                                return type === 'asc' ? -1 : 1
                            }
                            if (transformToLatin(a[objectKey]) > transformToLatin(b[objectKey])) {
                                return type === 'asc' ? 1 : -1
                            }
                            return 0
                        })
                    default:
                        return []
                }
            }
            default:
                return []
        }
    } else {
        return []
    }
}

export const fixedNumber = (number: number, rounding: number = 0.5) => {
    const numb = Number(number)

    if (numb > 0) {
        const plusNumb = Math.abs(numb - Math.floor(numb)) < rounding ? 0 : 1
        return Math.floor(numb) + plusNumb
    } else {
        const plusNumb = Math.abs(numb - Math.ceil(numb)) < rounding ? 0 : 1
        return Math.ceil(numb) - plusNumb
    }
}

export function normalizeToneMark(text: string): string {
    return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
}

export const textToLatin = (text: string) => {
    text = text.toString().toLowerCase().trim()

    const sets = [
        { to: 'a', from: '[ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶἀ]' },
        { to: 'c', from: '[ÇĆĈČ]' },
        { to: 'd', from: '[ÐĎĐÞ]' },
        { to: 'e', from: '[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ]' },
        { to: 'g', from: '[ĜĞĢǴ]' },
        { to: 'h', from: '[ĤḦ]' },
        { to: 'i', from: '[ÌÍÎÏĨĪĮİỈỊ]' },
        { to: 'j', from: '[Ĵ]' },
        { to: 'ij', from: '[Ĳ]' },
        { to: 'k', from: '[Ķ]' },
        { to: 'l', from: '[ĹĻĽŁ]' },
        { to: 'm', from: '[Ḿ]' },
        { to: 'n', from: '[ÑŃŅŇ]' },
        { to: 'o', from: '[ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ]' },
        { to: 'oe', from: '[Œ]' },
        { to: 'p', from: '[ṕ]' },
        { to: 'r', from: '[ŔŖŘ]' },
        { to: 's', from: '[ßŚŜŞŠȘ]' },
        { to: 't', from: '[ŢŤ]' },
        { to: 'u', from: '[ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ]' },
        { to: 'w', from: '[ẂŴẀẄ]' },
        { to: 'x', from: '[ẍ]' },
        { to: 'y', from: '[ÝŶŸỲỴỶỸ]' },
        { to: 'z', from: '[ŹŻŽ]' }
    ]

    sets.forEach((set) => {
        text = text.replace(new RegExp(set.from, 'gi'), set.to)
    })

    return text
}

export function checkIfEmailInString(text: string) {
    const regex =
        /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/
    return regex.test(text)
}

const addrToNumber = (addr: string) => {
    return addr.split('.').reduce((acc, cur, i) => (acc += Number(cur) << ((3 - i) * 8)), 0)
}

const subnetMaskToNumber = (mask: string) => {
    return (0xffffffff << (32 - Number(mask))) & 0xffffffff
}

export const isIPv4FromGoogle = (userIP: string): boolean => {
    const googleBotIPRange = [
        '66.249.64.0/27',
        '66.249.64.128/27',
        '66.249.64.160/27',
        '66.249.64.192/27',
        '66.249.64.224/27',
        '66.249.64.32/27',
        '66.249.64.64/27',
        '66.249.64.96/27',
        '66.249.65.0/27',
        '66.249.65.128/27',
        '66.249.65.160/27',
        '66.249.65.192/27',
        '66.249.65.224/27',
        '66.249.65.32/27',
        '66.249.65.64/27',
        '66.249.65.96/27',
        '66.249.66.0/27',
        '66.249.66.128/27',
        '66.249.66.192/27',
        '66.249.66.32/27',
        '66.249.66.64/27',
        '66.249.68.0/27',
        '66.249.68.32/27',
        '66.249.68.64/27',
        '66.249.69.0/27',
        '66.249.69.128/27',
        '66.249.69.160/27',
        '66.249.69.192/27',
        '66.249.69.224/27',
        '66.249.69.32/27',
        '66.249.69.64/27',
        '66.249.69.96/27',
        '66.249.70.0/27',
        '66.249.70.128/27',
        '66.249.70.160/27',
        '66.249.70.192/27',
        '66.249.70.224/27',
        '66.249.70.32/27',
        '66.249.70.64/27',
        '66.249.70.96/27',
        '66.249.71.0/27',
        '66.249.71.128/27',
        '66.249.71.160/27',
        '66.249.71.192/27',
        '66.249.71.32/27',
        '66.249.71.64/27',
        '66.249.71.96/27',
        '66.249.72.0/27',
        '66.249.72.128/27',
        '66.249.72.160/27',
        '66.249.72.192/27',
        '66.249.72.224/27',
        '66.249.72.32/27',
        '66.249.72.64/27',
        '66.249.72.96/27',
        '66.249.73.0/27',
        '66.249.73.128/27',
        '66.249.73.160/27',
        '66.249.73.192/27',
        '66.249.73.224/27',
        '66.249.73.32/27',
        '66.249.73.64/27',
        '66.249.73.96/27',
        '66.249.74.0/27',
        '66.249.74.32/27',
        '66.249.74.64/27',
        '66.249.74.96/27',
        '66.249.75.0/27',
        '66.249.75.128/27',
        '66.249.75.160/27',
        '66.249.75.192/27',
        '66.249.75.224/27',
        '66.249.75.32/27',
        '66.249.75.64/27',
        '66.249.75.96/27',
        '66.249.76.0/27',
        '66.249.76.128/27',
        '66.249.76.160/27',
        '66.249.76.192/27',
        '66.249.76.224/27',
        '66.249.76.32/27',
        '66.249.76.64/27',
        '66.249.76.96/27',
        '66.249.77.0/27',
        '66.249.77.128/27',
        '66.249.77.32/27',
        '66.249.77.64/27',
        '66.249.77.96/27',
        '66.249.79.0/27',
        '66.249.79.128/27',
        '66.249.79.160/27',
        '66.249.79.192/27',
        '66.249.79.224/27',
        '66.249.79.32/27',
        '66.249.79.64/27',
        '66.249.79.96/27',
        '66.249.91.0/27'
    ]
    let isFromGoogle = false
    // Loop through all CIDR from google IPv4 to find match IP
    googleBotIPRange.map((cidr: string) => {
        const [networkAddr, subnetMask] = cidr.split('/')
        const match = (addrToNumber(userIP) & subnetMaskToNumber(subnetMask)) === addrToNumber(networkAddr)
        if (match) {
            isFromGoogle = true
        }
    })

    return isFromGoogle
}
// For IPv6
const isIPv6FromGoogle = (userIP: string): boolean => {
    const googleBotIPv6CIDR = [
        '2001:4860:4801:10::/64',
        '2001:4860:4801:11::/64',
        '2001:4860:4801:12::/64',
        '2001:4860:4801:13::/64',
        '2001:4860:4801:14::/64',
        '2001:4860:4801:15::/64',
        '2001:4860:4801:16::/64',
        '2001:4860:4801:17::/64',
        '2001:4860:4801:18::/64',
        '2001:4860:4801:19::/64',
        '2001:4860:4801:1a::/64',
        '2001:4860:4801:1b::/64',
        '2001:4860:4801:20::/64',
        '2001:4860:4801:21::/64',
        '2001:4860:4801:22::/64',
        '2001:4860:4801:23::/64',
        '2001:4860:4801:24::/64',
        '2001:4860:4801:25::/64',
        '2001:4860:4801:26::/64',
        '2001:4860:4801:27::/64',
        '2001:4860:4801:28::/64',
        '2001:4860:4801:29::/64',
        '2001:4860:4801:2::/64',
        '2001:4860:4801:2a::/64',
        '2001:4860:4801:2b::/64',
        '2001:4860:4801:2c::/64',
        '2001:4860:4801:2d::/64',
        '2001:4860:4801:2e::/64',
        '2001:4860:4801:2f::/64',
        '2001:4860:4801:30::/64',
        '2001:4860:4801:31::/64',
        '2001:4860:4801:32::/64',
        '2001:4860:4801:33::/64',
        '2001:4860:4801:34::/64',
        '2001:4860:4801:35::/64',
        '2001:4860:4801:36::/64',
        '2001:4860:4801:37::/64',
        '2001:4860:4801:38::/64',
        '2001:4860:4801:39::/64',
        '2001:4860:4801:3::/64',
        '2001:4860:4801:3a::/64',
        '2001:4860:4801:3b::/64',
        '2001:4860:4801:3c::/64',
        '2001:4860:4801:3d::/64',
        '2001:4860:4801:3e::/64',
        '2001:4860:4801:40::/64',
        '2001:4860:4801:41::/64',
        '2001:4860:4801:42::/64',
        '2001:4860:4801:43::/64',
        '2001:4860:4801:44::/64',
        '2001:4860:4801:45::/64',
        '2001:4860:4801:46::/64',
        '2001:4860:4801:47::/64',
        '2001:4860:4801:48::/64',
        '2001:4860:4801:49::/64',
        '2001:4860:4801:4a::/64',
        '2001:4860:4801:50::/64',
        '2001:4860:4801:51::/64',
        '2001:4860:4801:53::/64',
        '2001:4860:4801:60::/64',
        '2001:4860:4801:61::/64',
        '2001:4860:4801:62::/64',
        '2001:4860:4801:63::/64',
        '2001:4860:4801:64::/64',
        '2001:4860:4801:65::/64',
        '2001:4860:4801:66::/64',
        '2001:4860:4801:67::/64',
        '2001:4860:4801:68::/64',
        '2001:4860:4801:69::/64',
        '2001:4860:4801:6a::/64',
        '2001:4860:4801:6b::/64',
        '2001:4860:4801:6c::/64',
        '2001:4860:4801:6d::/64',
        '2001:4860:4801:6e::/64',
        '2001:4860:4801:6f::/64',
        '2001:4860:4801:70::/64',
        '2001:4860:4801:71::/64',
        '2001:4860:4801:72::/64',
        '2001:4860:4801:73::/64',
        '2001:4860:4801:74::/64',
        '2001:4860:4801:75::/64',
        '2001:4860:4801:76::/64',
        '2001:4860:4801:77::/64',
        '2001:4860:4801:80::/64',
        '2001:4860:4801:81::/64',
        '2001:4860:4801:82::/64',
        '2001:4860:4801:83::/64',
        '2001:4860:4801:84::/64',
        '2001:4860:4801:85::/64',
        '2001:4860:4801:86::/64',
        '2001:4860:4801:90::/64',
        '2001:4860:4801:91::/64',
        '2001:4860:4801:92::/64',
        '2001:4860:4801::/64',
        '2001:4860:4801:c::/64',
        '2001:4860:4801:f::/64'
    ]
    const isFromGoogle = false
    // TODO: Loop through all CIDR from google IPv6 to find match IP
    // IPv6 is not support yet
    return isFromGoogle
}

/**
 * Check user IP address if it is a bot or not
 * @param userIP string
 */
const isIPFromGoogleBot = (userIP: string[]): boolean => {
    if (userIP.length === 0) {
        return false
    }

    let isFromGoogle = false

    for (let i = 0; i < userIP.length; i++) {
        if (!userIP[i]) {
            continue
        }

        const isIPv4 = userIP[i].indexOf('.') > 0
        const userIPSplitted = isIPv4 ? userIP[i].split('.') : userIP[i].split(':')

        if (isIPv4 && userIPSplitted[0] === '66' && userIPSplitted[1] === '249') {
            isFromGoogle = isIPv4FromGoogle(userIP[i])
        } else if (
            !isIPv4 &&
            userIPSplitted.length > 1 &&
            userIPSplitted[0] === '2001' &&
            userIPSplitted[1] === '4860' &&
            userIPSplitted[2] === '4801'
        ) {
            isFromGoogle = isIPv6FromGoogle(userIP[i])
        }
    }

    return isFromGoogle
}

/**
 * Check a user-agent if it's a robot
 * @param userAgent
 */
export const isRobot = (userAgent: string, userIP: string[]): boolean => {
    const botUserAgents = [
        'googlebot',
        'google-structured-data-testing-tool',
        'bingbot',
        'linkedinbot',
        'mediapartners-google',
        'apis-google',
        'feedfetcher-google',
        'googlebot-image',
        'storebot-google',
        'adsBot-google-mobile',
        'adsBot-google-mobile-apps',
        'adsBot-google',
        'googlebot-news',
        'googlebot-video',
        'storebot-google'
    ]

    const currentUserAgent: string = userAgent.toLowerCase()

    let isBot = botUserAgents.some((item) => {
        return currentUserAgent.includes(item)
    })

    // Detect IP from Google bot
    // Only call detect IP when isBot = false (For better performance)
    if (!isBot && userIP !== undefined) {
        isBot = isIPFromGoogleBot(userIP)
    }

    return isBot
}
/**
 * Detect Safari browser
 * @returns boolean is safari or not
 */
export const isSafari = (): boolean => {
    // Default is true
    if (typeof navigator === 'undefined') {
        return true
    }
    if (typeof navigator.userAgent === 'undefined') {
        return true
    }
    if (navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1) {
        return true
    }

    return false
}

/**
 * Check company is whitelist or not
 * @param companyId company id
 * @returns boolean is company id whitelist or not
 */
export type TCompanyProps = {
    companyId: number
    appMode: 'staging' | 'release' | 'production'
}
export const isCompanyWhitelisted = (props: TCompanyProps): boolean => {
    const { companyId, appMode } = props
    // staging
    if (appMode === 'staging') {
        return NAVIGOS_COMPANY_ID.staging.includes(companyId)
    }
    // release
    if (appMode === 'release') {
        return NAVIGOS_COMPANY_ID.release.includes(companyId)
    }
    // production
    return NAVIGOS_COMPANY_ID.production.includes(companyId)
}

/**
 * Splitted string into each child without cutting off word
 * @param text input text need to split
 * @param maxChar max number character length
 * @param fontSizeLevel default fontSizeLevel
 * @returns list of string being cutted without cutting off word
 */
export const splitString = (text: string, maxChar: number) => {
    if (text.length <= maxChar) {
        return [text]
    }

    const textArray: string[] = []
    let remainingText = text
    while (remainingText.length > maxChar) {
        let splitIndex = remainingText.lastIndexOf(' ', maxChar)
        if (splitIndex === -1 || splitIndex < maxChar * 0.5) {
            splitIndex = maxChar
        }
        const splittedText = remainingText.substring(0, splitIndex).trim()
        textArray.push(splittedText)
        remainingText = remainingText.substring(splitIndex).trim()
    }

    if (remainingText.length > 0) {
        textArray.push(remainingText)
    }

    return textArray
}

export const filterXSS = (text: string) => {
    return text.replace(/<script>/g, '').replace(/<\/script>/g, '')
}

/**
 * Return Random UID - @SangNguyen
 * @returns unique id (Ex: rand-bdc08475-7af48ce5-9da0d3be)
 */

export const randomId = (): string => {
    const s4 = () => {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1)
    }
    return `rand-${s4()}${s4()}-${s4()}${s4()}-${s4()}${s4()}`
}
/**
 * Check values change
 * @param firstLocations first item locations need to compare
 * @param secondLocations second item locations need to compare
 * @returns boolean true when 2 variables is different
 */
export function isLocationChanged(firstLocations: TNestedGenericId[], secondLocations: TNestedGenericId[]) {
    // parents is have more or less item
    if (firstLocations.length !== secondLocations.length) {
        return true
    }
    // Check districts
    for (let i = 0; i < firstLocations.length; i++) {
        const currentItem = firstLocations[i]
        const filtered = secondLocations.filter((item) => item?.parentId === currentItem?.parentId)
        // length match then detect child item
        // second item doesn't have item that first item have
        if (filtered.length === 0) {
            return true
        }
        // Child is have more or less selected
        if (filtered[0]?.childrenIds?.length !== currentItem?.childrenIds?.length) {
            return true
        }
        // length does match need to compare child
        for (let j = 0; j < currentItem?.childrenIds?.length; j++) {
            const childId = currentItem?.childrenIds[j]
            const filteredChild = filtered[0]?.childrenIds?.filter((childItemId) => childItemId === childId)
            if (filteredChild.length === 0) {
                return true
            }
        }
    }

    return false
}

export function formatSalary({ min, max }: { min: number; max: number }, isShowSalary: boolean, language: string) {
    if (!isShowSalary) {
        return language === 'en' ? 'Negotiate' : 'Thương Lượng'
    }

    if (min > 0 && max > 0) {
        return `$${min} - $${max}`
    }

    if (min > 0) {
        return `${language === 'en' ? 'From' : 'Từ'} $${min}`
    }

    if (max > 0) {
        return `${language === 'en' ? 'Up To' : 'Tới'} $${max}`
    }

    return ''
}
export function fullTrim(text: string): string {
    return text.trim().replace(/\s\s+/g, ' ')
}

export function isVietnamese(text: string) {
    return /[\u0300-\u036f]|[đĐ]/gi.test(text.normalize('NFD'))
}

export function createTranslator<T extends Record<string, string>>({
    translations,
    defaultLanguage,
    currentLanguage
}: {
    translations: T
    defaultLanguage?: ELanguage
    currentLanguage: ELanguage
}): (str: keyof T, replacement?: Record<string, any>) => string {
    let firstEntry = null

    let detectedDefaultLanguage = defaultLanguage
    if (!detectedDefaultLanguage) {
        for (const [keyText, valueText] of Object.entries(translations)) {
            if (!firstEntry) {
                firstEntry = [keyText, valueText]
            }
            if (isVietnamese(keyText)) {
                detectedDefaultLanguage = ELanguage.VI
                break
            }
            if (isVietnamese(valueText)) {
                detectedDefaultLanguage = ELanguage.EN
                break
            }
        }
    }
    if (!detectedDefaultLanguage) {
        console.warn(`Cannot detect default language from provided dictionary. First entry is "${firstEntry}"`)
        console.warn(`Fallback to English as default language`)
        detectedDefaultLanguage = ELanguage.EN
    }

    return (text, replacement?) => {
        if (!text) {
            return ''
        }
        let matchedText = currentLanguage === detectedDefaultLanguage ? text : translations[text]
        if (!matchedText) {
            matchedText = text
        }
        return replacement
            ? Object.keys(replacement).reduce(
                  (acc: string, key) => acc.replace(new RegExp(key, 'g'), replacement[key].toString() as string),
                  matchedText as string
              )
            : (matchedText as string)
    }
}

// this fnc using when not using state in input => using custom debounce
// for example in Form management

export function debounceHandler<T extends any[]>(func: (...args: T) => void, delay: number): (...args: T) => void {
    let timer: ReturnType<typeof setTimeout>

    return function (this: typeof func, ...args: T) {
        clearTimeout(timer)
        timer = setTimeout(() => {
            func.apply(this, args)
        }, delay)
    }
}

export function attachQueryParamToUrl(url: string, params: Record<string | number, any>): string {
    if (!Object.keys(params).length) {
        return url
    }

    const urlEntity = new URL(url)

    for (const [key, value] of Object.entries(params)) {
        if (value === undefined) {
            urlEntity.searchParams.delete(key)
            continue
        }
        if ((typeof value === 'object' && !Array.isArray(value)) || Number.isNaN(value)) {
            continue
        }
        urlEntity.searchParams.set(key, value)
    }

    return urlEntity.toString()
}

export type TDownloadRequest = 'download' | 'new-window' | 'all'
export interface IDownloadAttachFileParams {
    resumeId: number
    accessToken: string
    msBaseUrl: string
    fileName: string
    body?: any
    downloadRequest?: TDownloadRequest
    onOpenOrDownload?: () => void
}

function extractFileNameAndExtension(fileName: string): [name: string, extension: string] {
    const lastDotIndex = fileName?.lastIndexOf('.')
    const name = fileName?.slice(0, lastDotIndex)
    const extension = fileName?.slice(lastDotIndex + 1)

    return [name, extension]
}

export async function downloadAttachFile(params: IDownloadAttachFileParams) {
    try {
        const {
            resumeId,
            accessToken,
            msBaseUrl,
            fileName,
            downloadRequest = 'all',
            body = null,
            onOpenOrDownload = null
        } = params
        if (!accessToken) {
            return
        }
        const docExtensions = ['docx', 'doc']
        const pdfMineType = 'application/pdf'
        const API_DOWNLOAD_CV = `${msBaseUrl}/api-gateway/v1.0/profile/resume-file/${resumeId}`

        const requestOptions: {
            method: string
            headers: any
            body?: string
        } = {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        if (body !== null) {
            requestOptions.headers['Content-Type'] = 'application/json'
            requestOptions.body = JSON.stringify(body)
        }

        const response = await fetch(API_DOWNLOAD_CV, requestOptions)

        const blob = await response.blob()
        const file = window.URL.createObjectURL(blob)
        const [extractedFilename, fileExtension] = extractFileNameAndExtension(fileName)
        const responseMineType = response?.headers?.get('Content-Type')

        let requestState = downloadRequest
        if (docExtensions.includes(fileExtension) && responseMineType !== pdfMineType) {
            requestState = 'download'
        }
        // Download on current tab
        const download = () => {
            const a = document.createElement('a')
            a.href = file
            a.download = transformToLatin(`${extractedFilename}.${fileExtension}`)
            a.click()
            onOpenOrDownload && onOpenOrDownload()
        }
        // Open new tab
        const openNewTab = () => {
            onOpenOrDownload && onOpenOrDownload()
            if (typeof window === 'undefined') {
                return
            }
            window.open(file, '_blank')
        }

        switch (requestState) {
            case 'download':
                download()
                break
            case 'new-window':
                openNewTab()
                break
            case 'all':
            default:
                download()
                openNewTab()
                break
        }
    } catch (ex) {
        console.error('Error when download file', ex)
    }
}

export function createFormData(body: Record<string, any>): FormData {
    const formData = new FormData()
    for (const [key, value] of Object.entries(body)) {
        if (key && value !== null && value !== undefined) {
            formData.append(key, value)
        }
    }
    return formData
}

const ACCESS_TOKEN_LENGTH = 86
export const detectEncryptAccessToken = (accessToken: string) => {
    // If access token length is larger than 86 characters
    // then it should be decrypted
    return accessToken.length === ACCESS_TOKEN_LENGTH ? accessToken : decrypt(accessToken ?? '')
}

export function getiOSVersion() {
    if (typeof window === 'undefined') {
        return -2
    }
    const userAgent = window.navigator.userAgent

    const match = userAgent.match(/(iPad|iPhone|iPod)\s+OS\s+(\d+)/)

    if (!match) {
        // Not iOS
        return -2
    }
    if (match && match[2]) {
        const versionInNumber = Number(match[2])
        if (isNaN(versionInNumber)) {
            return -1
        }
        return versionInNumber
    }
    // -1 is standing for Unknown
    return -1
}

export function isPrivateIP(ip: string): boolean {
    // Localhost range
    const privateRanges = [
        { start: '10.0.0.0', end: '10.255.255.255' },
        { start: '172.16.0.0', end: '172.31.255.255' },
        { start: '192.168.0.0', end: '192.168.255.255' },
        { start: '127.0.0.0', end: '127.255.255.255' }
    ]

    const ipToNumber = (ip: string): number => {
        return ip.split('.').reduce((acc, octet) => acc * 256 + parseInt(octet, 10), 0)
    }

    const ipNumber = ipToNumber(ip.trim())

    return privateRanges.some((range) => {
        return ipNumber >= ipToNumber(range.start) && ipNumber <= ipToNumber(range.end)
    })
}

export const filterPrivateIP = (ipList: string[]): string[] => {
    // Filter out all Fortiweb IP
    const filteredNotFortiweb = ipList.filter((ip) => !FORTIWEB_IP.includes(ip))
    // Filter out all private IP
    const filteredPublic = filteredNotFortiweb.filter((ip) => !isPrivateIP(ip))

    if (filteredPublic.length > 0) {
        return filteredPublic
    }
    // If no public IP remain then return filtered not fortiweb
    return filteredNotFortiweb
}

/*
 * Forked of https://github.com/epoberezkin/fast-deep-equal
 * */
export function deepEqual(a: any, b: any): boolean {
    if (a === b) {
        return true
    }
    if (typeof a !== typeof b) {
        return false
    }

    if (a && b && typeof a === 'object' && typeof b === 'object') {
        if (a.constructor !== b.constructor) {
            return false
        }

        let length, i

        if (Array.isArray(a)) {
            length = a.length

            if (length !== b.length) {
                return false
            }

            for (i = length; i-- !== 0; ) {
                if (!deepEqual(a[i], b[i])) {
                    return false
                }
            }

            return true
        }

        const keys = Object.keys(a)
        length = keys.length

        if (length !== Object.keys(b).length) {
            return false
        }

        for (i = length; i-- !== 0; ) {
            if (!{}.hasOwnProperty.call(b, keys[i])) {
                return false
            }
        }

        for (i = length; i-- !== 0; ) {
            const key = keys[i]
            if (key === '_owner' && a.$$typeof) {
                continue
            }

            if (!deepEqual(a[key], b[key])) {
                return false
            }
        }

        return true
    }
    return a !== a && b !== b
}

export function filterObjectArrayBasedOnObjectArray(targetArray: any[], filterArray: any, key: string, omit?: any[]) {
    let filterArrayKeys = filterArray.map((item: any) => item[key])
    if (omit && omit.length > 0) {
        filterArrayKeys = filterArrayKeys.concat(omit)
    }
    return targetArray.filter((item) => !filterArrayKeys.includes(item[key]))
}

export function quickFilterByKeywords(options: any[], keywords: string, fieldSearch: string) {
    const cpOptions = [...options]
    return cpOptions.filter((option) => {
        if (keywords === '') {
            return true
        }
        return transformToLatin(option[fieldSearch]).indexOf(transformToLatin(keywords)) > -1
    })
}

export function locationsMappingHelperForMultiLevelSelect(
    countries: TSelectDataItem[],
    cities: TSelectDataItem[],
    districts: { [key: number]: TOrderedSelectDataItem[] },
    langcode: ELanguage
) {
    let mappedVietNamCities: any[] = []
    cities.forEach((city) => {
        if (districts[city.id]) {
            const result = {
                ...city,
                name: langcode === ELanguage.VI ? city.name : city.nameEn,
                children: districts[city.id].map((district) => ({
                    ...district,
                    name: langcode === ELanguage.VI ? district.name : district.nameEn
                }))
            }
            mappedVietNamCities.push(result)
        } else {
            mappedVietNamCities.push({ ...city, name: langcode === ELanguage.VI ? city.name : city.nameEn })
        }
    })

    mappedVietNamCities = mappedVietNamCities.filter((city) => city.id !== INTERNATIONAL_CITY_ID)

    const mappedCountries: any[] = []

    countries.forEach((country) => {
        if (country.id === VIETNAM_COUNTRY_ID) {
            const result = {
                ...country,
                name: langcode === ELanguage.VI ? country.name : country.nameEn,
                children: mappedVietNamCities
            }
            mappedCountries.push(result)
        } else {
            const result = {
                ...country,
                name: langcode === ELanguage.VI ? country.name : country.nameEn
            }
            mappedCountries.push(result)
        }
    })

    return mappedCountries
}

/**
 * Deeply merge two or more objects
 * @param  {...object} objects - Objects to merge
 */
export function deepMerge(...objects: any) {
    const target: any = {}
    const merge = (obj: any) => {
        for (const prop in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, prop)) {
                if (obj[prop] === null || obj[prop] === undefined) {
                    continue
                }
                if (typeof obj[prop] === 'object' && !Array.isArray(obj[prop])) {
                    target[prop] = deepMerge(target[prop], obj[prop])
                } else if (typeof obj[prop] === 'object' && Array.isArray(obj[prop])) {
                    target[prop] = [...(target[prop] || []), ...(obj[prop] || [])]
                } else {
                    target[prop] = obj[prop]
                }
            }
        }
    }
    objects.forEach((object: any) => merge(object))
    return target
}
